import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { hardwareDevices } from "../../actions/doctorActions";
import Table from "../react-table-component/ReactTable";
import { formatDate } from "../../helper";
import { Link } from "react-router-dom";
import { DropDownItem } from "../header/Header";
import historyIcon from "../../assets/img/svg/history-icon.svg";
import editIcon from "../../assets/img/svg/edit-icon.svg";
import dotMenuIcon from "../../assets/img/svg/dots-menu-icon.svg";
import trashIcon from "../../assets/img/svg/trash-can-2.svg";
import { HardwareDeleteConfirmModal } from "./HardwareDeleteConfirmModal";
import HardwareListFilterCard, {
  DEVICE_STATUSES,
} from "./HardwareListFilterCard";
import StatsCard from "../patients/RecordingAdminStats";
import "../patients/patients.css";

export default function HardwareList() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const { loading, devices } = useSelector(
    (state) => state.hardwareDevicesList,
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [id, setId] = useState(null);

  const [searchField, setSearchField] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 100 : prev + 20));
      }, 400);
      return () => clearInterval(interval);
    };
    simulateProgress();
    dispatch(hardwareDevices(token)).finally(() => setProgress(100));
  }, [dispatch, token]);

  const handleSearch = (e) => setSearchField(e.target.value);

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters || {});
    if (
      filters?.dateFrom ||
      filters?.dateTo ||
      filters?.hardwareRevision ||
      filters?.status ||
      filters?.isDamaged
    ) {
      toast.success("Filter applied successfully!");
    } else {
      toast.info("All filters cleared");
    }
  };

  const hardwareRevisionOptions = React.useMemo(() => {
    const set = new Set();
    (devices || []).forEach((d) => {
      if (d?.hardware_revision) set.add(d.hardware_revision);
    });
    return Array.from(set);
  }, [devices]);

  const filteredDevices = React.useMemo(() => {
    if (!Array.isArray(devices)) return [];

    const searchValue = searchField?.toLowerCase() || "";
    const hasDateFilter = appliedFilters?.dateFrom || appliedFilters?.dateTo;

    const from = appliedFilters?.dateFrom
      ? new Date(appliedFilters.dateFrom)
      : null;
    const to = appliedFilters?.dateTo ? new Date(appliedFilters.dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return devices.filter((item) => {
      const serial = (item.serial_number || "").toLowerCase();
      const batch = (item.batch_number || "").toLowerCase();
      const ble = (item.ble_address || "").toLowerCase();
      const matchesSearch =
        !searchValue ||
        serial.includes(searchValue) ||
        batch.includes(searchValue) ||
        ble.includes(searchValue);

      if (!matchesSearch) return false;

      if (hasDateFilter) {
        if (!item.manufacture_date) return false;
        const mfg = new Date(item.manufacture_date);
        if (Number.isNaN(mfg.getTime())) return false;
        if (from && mfg < from) return false;
        if (to && mfg > to) return false;
      }

      if (
        appliedFilters?.hardwareRevision &&
        item.hardware_revision !== appliedFilters.hardwareRevision
      ) {
        return false;
      }

      if (appliedFilters?.status && item.status !== appliedFilters.status) {
        return false;
      }

      // if (appliedFilters?.isDamaged === "yes" && item.is_damaged !== true) {
      //   return false;
      // }
      // if (appliedFilters?.isDamaged === "no" && item.is_damaged === true) {
      //   return false;
      // }

      return true;
    });
  }, [devices, searchField, appliedFilters]);

  const statsData = React.useMemo(() => {
    const list = filteredDevices;
    const byStatus = Object.fromEntries(DEVICE_STATUSES.map((s) => [s, 0]));
    let damagedYes = 0;
    let damagedNo = 0;

    list.forEach((d) => {
      const st = d.status;
      if (st && Object.prototype.hasOwnProperty.call(byStatus, st)) {
        byStatus[st] += 1;
      }
      if (d.is_damaged === true) damagedYes += 1;
      else damagedNo += 1;
    });

    const statusStats = DEVICE_STATUSES.map((s) => ({
      label: s,
      value: byStatus[s],
    }));

    return [
      { label: "Devices", value: list.length },
      ...statusStats,
      // { label: "Damaged (yes)", value: damagedYes },
      // { label: "Damaged (no)", value: damagedNo },
    ];
  }, [filteredDevices]);

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);
  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const columns = React.useMemo(() => [
    { Header: "Serial No", accessor: "serial_number" },
    { Header: "Batch No", accessor: "batch_number" },
    { Header: "BLE Address", accessor: "ble_address" },
    {
      Header: "Manufacture Date",
      accessor: "manufacture_date",
      Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
    },
    { Header: "Hardware Rev", accessor: "hardware_revision" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => value || "N/A",
    },
    {
      Header: "Passed Test",
      accessor: "passed_testing",
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      Header: "Is Damaged",
      accessor: "is_damaged",
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      Header: "",
      accessor: "action",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isMenuOpen = openMenuRowIndex === row.index;
        return (
          <div className="dot-menu-icon">
            <img
              src={dotMenuIcon}
              alt="dot menu"
              onClick={() => toggleMenuForRow(row.index)}
            />

            {isMenuOpen && (
              <div ref={dotMenuRef} className="patients-dot-menu">
                <DropDownItem
                  item={{
                    title: "View",
                    icon: historyIcon,
                    to: `/hardware/${row?.original?.id}`,
                  }}
                />
                <DropDownItem
                  item={{
                    title: "Edit",
                    icon: editIcon,
                    to: `/hardware-update/${row?.original?.id}`,
                  }}
                />
                <div className="delete-clr">
                  <DropDownItem
                    item={{
                      title: "Delete",
                      icon: trashIcon,
                    }}
                    handleClick={() => {
                      setId(row?.original?.id);
                      setShowDeleteDialog(true);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ]);

  return (
    <div className="patient-list">
      <HardwareDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false);
        }}
        id={id}
        token={token}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0 }}>Hardware List</h3>
        <Link to={`/hardware-create`}>
          <button
            className="button btn-default"
            style={{
              padding: "0.35rem 0.7rem",
              fontSize: "0.9rem",
              fontWeight: "bold",
            }}
          >
            Add Device
          </button>
        </Link>
      </div>

      <HardwareListFilterCard
        onApplyFilter={handleApplyFilter}
        currentFilters={appliedFilters}
        searchValue={searchField}
        onSearchChange={handleSearch}
        hardwareRevisionOptions={hardwareRevisionOptions}
      />

      <div className="tw-p-2">
        <StatsCard stats={statsData} />
      </div>

      <Table
        columns={columns}
        data={filteredDevices}
        loading={loading}
        tableOf="HardwareDevices"
        progress={progress}
      />
    </div>
  );
}
